import React, { useState } from 'react';
import { ComplianceCheck } from '../../utils/types';
import { crosscheckState, isDisputed } from './crosscheck/state';
import {
  ConfirmedChip,
  CrosscheckQuote,
  CrosscheckRow,
  DisputedChip,
  DisputedExplainLink,
  TwoReadings,
} from './crosscheck/CrosscheckParts';

interface PassingChecksProps {
  checks: ComplianceCheck[];
}

export default function PassingChecks({ checks }: PassingChecksProps) {
  const [open, setOpen] = useState(false);
  const passing = checks.filter((c) => c.status === 'compliant');

  if (passing.length === 0) return null;

  return (
    <section className="passing-checks">
      <button
        type="button"
        className="passing-checks__toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className="passing-checks__heading">
          <span className="report-section-header__num">02</span>
          <span className="passing-checks__title">What you have right</span>
          <span className="passing-checks__count">
            {passing.length} passing check{passing.length === 1 ? '' : 's'}
          </span>
        </span>
        <span className={`passing-checks__caret ${open ? 'passing-checks__caret--open' : ''}`} aria-hidden="true">
          ▼
        </span>
      </button>

      {open && (
        <div className="passing-checks__list">
          {passing.map((check) => (
            <PassingRow key={check.id} check={check} />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * One passing row. AI Crosscheck (#1659): a confirmed reading adds a chip and
 * an "agrees" row; a correction that made this check pass quotes the passage;
 * a dispute (the crosscheck rejects the matched text but was not applied)
 * shows both readings, since the check still counts as passing.
 */
function PassingRow({ check }: { check: ComplianceCheck }) {
  const adjudication = check.adjudication;
  const xcheck = crosscheckState(adjudication);
  const disputed = isDisputed(xcheck);
  const finding = check.finding || check.details || 'Check passed.';

  return (
    <div className="passing-check-row">
      <div className="passing-check-row__mark" aria-hidden="true">✓</div>
      <div className="passing-check-row__body">
        <div className="passing-check-row__name">
          {check.name}
          {disputed && <DisputedChip />}
          {xcheck === 'confirmed' && <ConfirmedChip />}
          {disputed && <DisputedExplainLink />}
        </div>
        {disputed && adjudication ? (
          <TwoReadings adjudication={adjudication} patternFound={xcheck === 'disputed-passed'} patternText={finding} />
        ) : (
          <div className="passing-check-row__finding">{finding}</div>
        )}
        {xcheck === 'confirmed' && (
          <CrosscheckRow label="AI Crosscheck agrees">
            {adjudication?.reasoning && <span className="crosscheck-row__text">{adjudication.reasoning}</span>}
          </CrosscheckRow>
        )}
        {xcheck === 'corrected-pass' && (
          <CrosscheckRow label="AI Crosscheck found this disclosure and corrected the verdict">
            {adjudication?.quote && <CrosscheckQuote quote={adjudication.quote} />}
          </CrosscheckRow>
        )}
      </div>
    </div>
  );
}
